import React from 'react';
import { Box, Button, Paper, Typography, alpha, useTheme } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import WifiTetheringIcon from '@mui/icons-material/WifiTethering';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { useUpcomingLiveQuizzes } from '../../hooks/useUpcomingLiveQuizzes';
import { useServerCountdown } from '../../hooks/useServerCountdown';
import { LiveEventSummary, LiveRoomStatus } from '../../types/liveQuiz';

type BannerPhase = 'upcoming' | 'lobby' | 'live' | 'liveJoined';

function isLiveNow(status: LiveRoomStatus | null): boolean {
  return status === 'question' || status === 'reveal' || status === 'scoreboard';
}

function isOver(status: LiveRoomStatus | null): boolean {
  return status === 'finished' || status === 'abandoned';
}

function phaseOf(event: LiveEventSummary): BannerPhase {
  if (event.status === 'lobby') {
    return 'lobby';
  }
  if (isLiveNow(event.status)) {
    return event.joined ? 'liveJoined' : 'live';
  }
  return 'upcoming';
}

/** Live/joined first, then lobby, then upcoming — each group soonest first. */
function phaseRank(phase: BannerPhase): number {
  return phase === 'live' || phase === 'liveJoined' ? 0 : phase === 'lobby' ? 1 : 2;
}

/** The deadline relevant to sorting within a phase — the same target its countdown uses. */
function sortTimeOf(event: LiveEventSummary): number {
  const phase = phaseOf(event);
  if (phase === 'lobby') {
    return Date.parse(event.startsAt);
  }
  if (phase === 'upcoming') {
    return Date.parse(event.lobbyOpensAt);
  }
  return 0;
}

/**
 * Which events the banner is allowed to show at all: ones the student
 * RSVP'd to, plus — regardless of RSVP — a running event they've already
 * joined, so a mid-quiz refresh gets them back in rather than stranding them.
 */
function isVisible(event: LiveEventSummary): boolean {
  if (isOver(event.status)) {
    return false;
  }
  if (event.rsvped) {
    return true;
  }
  return isLiveNow(event.status) && event.joined;
}

/**
 * "Starting soon / live now" reminder for events the student cares about.
 * Owns its own fetch (`useUpcomingLiveQuizzes`) so `DashboardPage` gains one
 * import and one JSX line. Renders nothing when there's nothing to show, so
 * it collapses without leaving a gap.
 */
const LiveQuizBanner: React.FC = () => {
  const theme = useTheme();
  const { data, refetch } = useUpcomingLiveQuizzes();
  const events = data?.events ?? [];

  const visible = [...events.filter(isVisible)].sort(
    (a, b) => phaseRank(phaseOf(a)) - phaseRank(phaseOf(b)) || sortTimeOf(a) - sortTimeOf(b)
  );

  const primary = visible[0] ?? null;
  const moreCount = Math.max(0, visible.length - 1);
  const phase = primary ? phaseOf(primary) : null;

  // Crossing either deadline is exactly when the background poll (as slow as
  // 60s) needs to feel instant instead — one immediate refetch on the
  // transition covers that gap.
  const targetIso =
    phase === 'upcoming' ? primary?.lobbyOpensAt ?? null : phase === 'lobby' ? primary?.startsAt ?? null : null;
  const { label } = useServerCountdown(targetIso, {
    onExpire: () => {
      void refetch();
    }
  });

  if (!primary || !phase) {
    return null;
  }

  const accentColor =
    phase === 'lobby'
      ? theme.palette.warning.main
      : phase === 'live' || phase === 'liveJoined'
        ? theme.palette.error.main
        : theme.palette.info.main;

  let subline: string;
  switch (phase) {
    case 'upcoming':
      subline = `Starts in ${label}`;
      break;
    case 'lobby':
      subline = `Lobby's open — starts in ${label}`;
      break;
    case 'live':
      subline = 'Live now!';
      break;
    case 'liveJoined':
      subline = `Question ${primary.currentQuestionIndex + 1} of ${primary.totalQuestions}`;
      break;
  }

  const ctaLabel =
    phase === 'lobby' ? 'Enter lobby' : phase === 'live' ? 'Join now' : phase === 'liveJoined' ? 'Back to quiz' : null;
  const isLive = phase === 'live' || phase === 'liveJoined';

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        mb: 4,
        borderRadius: 3,
        border: `1px solid ${alpha(accentColor, 0.3)}`,
        bgcolor: alpha(accentColor, 0.06)
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {isLive ? (
            <Box
              aria-hidden
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: accentColor,
                '@keyframes live-pulse': {
                  '0%, 100%': { opacity: 1 },
                  '50%': { opacity: 0.3 }
                },
                animation: 'live-pulse 1.5s ease-in-out infinite',
                '@media (prefers-reduced-motion: reduce)': {
                  animation: 'none'
                }
              }}
            />
          ) : phase === 'lobby' ? (
            <WifiTetheringIcon sx={{ color: accentColor, fontSize: 20 }} />
          ) : (
            <ScheduleIcon sx={{ color: accentColor, fontSize: 20 }} />
          )}
          <Box>
            <Typography fontWeight={700}>{primary.quizTitle}</Typography>
            <Typography variant="body2" color="text.secondary">
              {subline}
            </Typography>
          </Box>
        </Box>

        {ctaLabel && primary.roomId !== null && (
          <Button
            component={RouterLink}
            to={`/student/live/${primary.roomId}`}
            variant={isLive ? 'contained' : 'outlined'}
            color={phase === 'lobby' ? 'warning' : isLive ? 'error' : 'primary'}
            sx={{ textTransform: 'none' }}
          >
            {ctaLabel}
          </Button>
        )}
      </Box>

      {moreCount > 0 && (
        <Typography variant="caption" sx={{ mt: 1.5, display: 'block' }}>
          <RouterLink to="/student/quizzes?tab=live">
            +{moreCount} more live event{moreCount === 1 ? '' : 's'}
          </RouterLink>
        </Typography>
      )}
    </Paper>
  );
};

export default LiveQuizBanner;
